import { config } from '@/config'
import leadAppIcon from '@/assets/apps/lead-app.png'
import mediathekIcon from '@/assets/apps/mediathek.png'

export interface MobileApp {
  /** The app's own name, like APP_NAME not a translation key: it reads the same in every language. */
  name: string
  /** The app's home screen icon, so the entry looks like what the phone will show afterwards. */
  icon: string
  /**
   * The install link: an itms-services link for an app distributed in-house, which installs it
   * straight away, or an App Store link, unlisted or not.
   */
  url: string
}

/** The companion iOS apps whose install link is configured, in sidebar order. */
export function buildMobileApps(urls: Pick<typeof config, 'mediathekUrl' | 'leadAppUrl'>): MobileApp[] {
  return [
    { name: 'WP Mediathek', icon: mediathekIcon, url: urls.mediathekUrl },
    { name: 'WP Lead App', icon: leadAppIcon, url: urls.leadAppUrl },
  ].filter((app): app is MobileApp => Boolean(app.url))
}

/** The apps of this build, from its environment. */
export const mobileApps: MobileApp[] = buildMobileApps(config)

/**
 * Where the sidebar runs, as far as the apps go: on an iPhone or iPad they install from the entry,
 * on a desktop from a QR code, and on any other phone or tablet not at all — the apps are iOS
 * only, and a QR code there would ask the user to scan their own screen.
 */
export type Platform = 'ios' | 'otherMobile' | 'desktop'

export function detectPlatform(
  nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator,
): Platform {
  if (isIOS(nav)) {
    return 'ios'
  }
  // "Mobi" is what phone browsers agree on; an Android tablet leaves it out but names Android.
  return /Android|Mobi/.test(nav.userAgent) ? 'otherMobile' : 'desktop'
}

/**
 * Whether this browser runs on an iPhone or iPad, where the install link can be followed directly
 * and a QR code would only ask the user to scan their own screen. iPadOS identifies as a Mac, so
 * a Mac with a touch screen counts too — no real Mac has one.
 */
export function isIOS(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): boolean {
  return (
    /iPhone|iPad|iPod/.test(nav.userAgent) ||
    (/Macintosh/.test(nav.userAgent) && nav.maxTouchPoints > 1)
  )
}

/**
 * Whether the link installs the app itself rather than opening the App Store. Only Safari on an
 * iPhone or iPad follows one; a desktop browser can do nothing with it.
 */
export function isDirectInstall(url: string): boolean {
  return url.startsWith('itms-services:')
}

/**
 * The host a person recognises. For a direct install that is the host serving the manifest — the
 * link itself is a wall of query string.
 */
export function displayUrl(url: string): string {
  if (isDirectInstall(url)) {
    const manifest = /[?&]url=([^&]+)/.exec(url)?.[1]
    if (manifest) {
      try {
        return new URL(decodeURIComponent(manifest)).host
      } catch {
        // Not a URL after all; the raw link below is still better than nothing.
      }
    }
  }
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
}
